import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { AlertCircle, ArrowLeft, CheckCircle2, Download, Eye, FileUp, Lock, Trash2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import DocumentPreviewDrawer from '../components/DocumentPreviewDrawer';
import api from '../services/api';
import { StyledCard } from './pfPraticaFormShared';

function formatShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function getStatusConfig(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'presente') {
    return {
      label: 'Presente',
      fg: '#2f5f13',
      bg: 'rgba(101, 163, 13, 0.16)',
      border: 'rgba(101, 163, 13, 0.18)',
      icon: <CheckCircle2 size={18} color="#4d7c0f" />
    };
  }
  if (normalized === 'scaduto') {
    return {
      label: 'Scaduto',
      fg: '#b45309',
      bg: 'rgba(245, 158, 11, 0.16)',
      border: 'rgba(245, 158, 11, 0.2)',
      icon: <AlertCircle size={18} color="#d97706" />
    };
  }
  return {
    label: 'Mancante',
    fg: '#8a5a12',
    bg: 'rgba(217, 119, 6, 0.14)',
    border: 'rgba(217, 119, 6, 0.18)',
    icon: <AlertCircle size={18} color="#d97706" />
  };
}

export default function PfPraticaDocumenti() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pratica, setPratica] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedTipoId, setSelectedTipoId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [noteDocumento, setNoteDocumento] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewOpenInNewTabLoading, setPreviewOpenInNewTabLoading] = useState(false);

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pf-documenti/pratiche/${id}`);
      setPratica(response.data?.pratica || null);
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setSummary(response.data?.summary || null);
      setError('');
    } catch (err) {
      console.error('Error fetching pf pratica documenti:', err);
      setError(err?.response?.data?.message || 'Errore nel caricamento dei documenti della pratica');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const tipiDocumentoOptions = useMemo(() => items.filter((item) => Number(item.tipo_documento_attivo) === 1), [items]);

  const selectedTipo = useMemo(
    () => tipiDocumentoOptions.find((item) => String(item.tipo_documento_id) === String(selectedTipoId)) || null,
    [selectedTipoId, tipiDocumentoOptions]
  );

  const handleOpenUploadDialog = (item = null) => {
    setSelectedTipoId(item?.tipo_documento_id ? String(item.tipo_documento_id) : '');
    setNoteDocumento(item?.note_documento || '');
    setSelectedFile(null);
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
    setSelectedTipoId('');
    setSelectedFile(null);
    setNoteDocumento('');
  };

  const handleUpload = async () => {
    if (!selectedTipoId) {
      toast.error('Seleziona il tipo documento');
      return;
    }
    if (!selectedFile) {
      toast.error('Seleziona un file da caricare');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('tipo_documento_id', selectedTipoId);
      formData.append('file', selectedFile);
      if (noteDocumento.trim()) {
        formData.append('note_documento', noteDocumento.trim());
      }

      await api.post(`/pf-documenti/pratiche/${id}/upload`, formData);

      toast.success(selectedTipo?.documento_id ? 'Documento sostituito con successo' : 'Documento caricato con successo');
      handleCloseUploadDialog();
      await loadChecklist();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore durante il caricamento del documento');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenPreviewDrawer = async (item) => {
    try {
      const documentoId = item?.documento_id ?? item?.id;
      if (!documentoId) {
        toast.error('Nessun file disponibile per questo documento');
        return;
      }
      setPreviewItem(item || null);
      setPreviewFileName(item?.nome_file || '');
      setPreviewFileUrl('');
      setPreviewDrawerOpen(true);
      setPreviewLoading(true);

      const response = await api.get(`/pf-documenti/${documentoId}/preview-url`);
      const url = response.data?.url;
      const documento = response.data?.documento || null;
      if (!url) {
        toast.error('Impossibile aprire il documento');
        return;
      }
      setPreviewFileUrl(url);
      if (documento) {
        setPreviewItem({
          ...(item || {}),
          ...documento,
          documento_id: item?.documento_id ?? documento?.id ?? item?.id ?? null
        });
        setPreviewFileName(documento?.nome_file || item?.nome_file || '');
      }
    } catch (err) {
      console.error('Error opening pf document preview:', err);
      toast.error(err?.response?.data?.message || 'Errore nell’apertura del documento');
      setPreviewDrawerOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      const documentoId = item?.documento_id ?? item?.id;
      if (!documentoId) {
        toast.error('Nessun file disponibile per questo documento');
        return;
      }
      const response = await api.get(`/pf-documenti/${documentoId}/download-url`);
      const url = response.data?.url;
      if (!url) {
        toast.error('Impossibile scaricare il documento');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error downloading pf document:', err);
      toast.error(err?.response?.data?.message || 'Errore durante il download del documento');
    }
  };

  const handleClosePreviewDrawer = () => {
    if (previewLoading) return;
    setPreviewDrawerOpen(false);
    setPreviewFileUrl('');
    setPreviewFileName('');
    setPreviewItem(null);
  };

  const handleOpenPreviewInNewTab = async () => {
    try {
      const documentoId = previewItem?.documento_id ?? previewItem?.id;
      if (!documentoId) return;
      setPreviewOpenInNewTabLoading(true);
      const response = await api.get(`/pf-documenti/${documentoId}/preview-url`);
      const url = response.data?.url;
      if (!url) {
        toast.error('Impossibile aprire il documento');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening pf document preview in new tab:', err);
      toast.error(err?.response?.data?.message || 'Errore nell’apertura del documento');
    } finally {
      setPreviewOpenInNewTabLoading(false);
    }
  };

  const previewDetails = useMemo(() => {
    if (!previewItem) return [];
    const fileName = previewItem.nome_file || previewFileName || '';
    const ext = fileName && fileName.includes('.') ? fileName.split('.').pop() : '';
    const format = ext ? String(ext).toUpperCase() : '-';
    const dataCaricamento = previewItem.data_caricamento
      ? new Date(previewItem.data_caricamento).toLocaleString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '-';

    return [
      { label: 'Nome', value: fileName || '-' },
      { label: 'Tipo', value: previewItem.tipo_documento_nome || '-' },
      { label: 'Formato', value: format },
      { label: 'Caricato il', value: dataCaricamento },
      { label: 'Caricato da', value: previewItem.caricato_da_nome || '-' },
      ...(previewItem.note_documento ? [{ label: 'Note', value: previewItem.note_documento }] : [])
    ];
  }, [previewItem, previewFileName]);

  const handleOpenDeleteDialog = (item) => {
    setDeleteTarget(item || null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.documento_id) return;
    setDeleting(true);
    try {
      await api.delete(`/pf-documenti/${deleteTarget.documento_id}`);
      toast.success('Documento eliminato con successo');
      handleCloseDeleteDialog();
      await loadChecklist();
    } catch (err) {
      console.error('Error deleting pf document:', err);
      toast.error(err?.response?.data?.message || 'Errore durante l’eliminazione del documento');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pratica) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Pratica non trovata'}
        </Alert>
        <Button variant="outline" onClick={() => navigate('/pratiche-finanziarie/pratiche')}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1320, mx: 'auto', display: 'grid', gap: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button variant="outline" onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}`)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Torna alla pratica
            </Button>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' }, fontWeight: 700, letterSpacing: 0.08, lineHeight: 1.15 }}>
                {`Documenti pratica #${pratica.id}`}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.9 }}>
                <Box
                  sx={{
                    px: 0.95,
                    py: 0.55,
                    borderRadius: 2,
                    bgcolor: 'rgba(37, 99, 235, 0.08)',
                    border: '1px solid',
                    borderColor: 'rgba(37, 99, 235, 0.14)',
                    minWidth: 128
                  }}
                >
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.24, color: '#1d4ed8', textTransform: 'uppercase', lineHeight: 1.1 }}>
                    Cliente
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.76rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
                    {pratica.cliente_label || '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 0.95,
                    py: 0.55,
                    borderRadius: 2,
                    bgcolor: 'rgba(15, 23, 42, 0.04)',
                    border: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    minWidth: 96
                  }}
                >
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.24, color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.1 }}>
                    Pratica
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.76rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
                    {`#${pratica.id}`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 0.95,
                    py: 0.55,
                    borderRadius: 2,
                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid',
                    borderColor: 'rgba(99, 102, 241, 0.14)',
                    minWidth: 148
                  }}
                >
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.24, color: '#4338ca', textTransform: 'uppercase', lineHeight: 1.1 }}>
                    Tipo operazione
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.76rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
                    {pratica.tipo_operazione_nome || '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 0.95,
                    py: 0.55,
                    borderRadius: 2,
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid',
                    borderColor: 'rgba(16, 185, 129, 0.14)',
                    minWidth: 108
                  }}
                >
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.24, color: '#047857', textTransform: 'uppercase', lineHeight: 1.1 }}>
                    Stato
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontSize: '0.76rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
                    {pratica.stato_nome_visualizzato || '-'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Button onClick={() => handleOpenUploadDialog()} className="gap-2 text-sm px-4 py-2">
            <Upload className="h-4 w-4" />
            Carica documento
          </Button>
        </Box>

        {error ? (
          <Alert severity="warning">
            {error}
          </Alert>
        ) : null}

        {summary ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Box sx={{ px: 1.35, py: 0.8, borderRadius: 999, bgcolor: 'rgba(15, 23, 42, 0.04)', fontSize: '0.85rem', fontWeight: 700 }}>
              {`${summary.totale || 0} tipi documento`}
            </Box>
            <Box sx={{ px: 1.35, py: 0.8, borderRadius: 999, bgcolor: 'rgba(101, 163, 13, 0.12)', color: '#3f6212', fontSize: '0.85rem', fontWeight: 700 }}>
              {`${summary.presenti || 0} presenti`}
            </Box>
            <Box sx={{ px: 1.35, py: 0.8, borderRadius: 999, bgcolor: 'rgba(217, 119, 6, 0.12)', color: '#9a3412', fontSize: '0.85rem', fontWeight: 700 }}>
              {`${summary.mancanti || 0} mancanti`}
            </Box>
            {(summary.scaduti || 0) > 0 ? (
              <Box sx={{ px: 1.35, py: 0.8, borderRadius: 999, bgcolor: 'rgba(234, 88, 12, 0.12)', color: '#c2410c', fontSize: '0.85rem', fontWeight: 700 }}>
                {`${summary.scaduti || 0} scaduti`}
              </Box>
            ) : null}
          </Stack>
        ) : null}

        <StyledCard>
          <Box sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gap: 0 }}>
            {items.map((item, index) => {
              const status = getStatusConfig(item.stato);
              const isPresent = item.stato === 'presente' && item.documento_id;
              const metaText = isPresent
                ? `${formatShortDate(item.data_caricamento)}${item.caricato_da_nome ? ` · ${item.caricato_da_nome}` : ''}`
                : item.stato === 'scaduto'
                  ? 'Documento da sostituire'
                  : 'Documento non ancora caricato';

              return (
                <Box
                  key={item.tipo_documento_id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.45fr) auto auto minmax(250px, auto)' },
                    gap: { xs: 1, md: 1.25 },
                    alignItems: 'center',
                    py: 1.45,
                    borderBottom: index === items.length - 1 ? 'none' : '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                    <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      {status.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: { xs: '1rem', md: '1.18rem' }, fontWeight: 600, lineHeight: 1.2 }}>
                        {item.tipo_documento_nome}
                      </Typography>
                      {item.nome_file ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, fontSize: '0.85rem' }}>
                          {item.nome_file}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box sx={{ justifySelf: { xs: 'start', md: 'end' } }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.2,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: status.bg,
                        color: status.fg,
                        border: '1px solid',
                        borderColor: status.border,
                        fontSize: '0.9rem',
                        fontWeight: 700
                      }}
                    >
                      {status.label}
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.9rem', minWidth: 110, textAlign: { xs: 'left', md: 'right' } }}
                  >
                    {metaText}
                  </Typography>

                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ justifySelf: { xs: 'start', md: 'end' } }}>
                    {isPresent ? (
                      <>
                        <Button variant="outline" onClick={() => handleOpenPreviewDrawer(item)} className="gap-2">
                          <Eye className="h-4 w-4" />
                          Vedi
                        </Button>
                        <Button variant="outline" onClick={() => handleDownload(item)} className="gap-2">
                          <Download className="h-4 w-4" />
                          Scarica
                        </Button>
                        <Button variant="outline" onClick={() => handleOpenUploadDialog(item)} className="gap-2">
                          <FileUp className="h-4 w-4" />
                          Sostituisci
                        </Button>
                        <Button variant="outline" onClick={() => handleOpenDeleteDialog(item)} className="gap-2">
                          <Trash2 className="h-4 w-4" />
                          Elimina
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={() => handleOpenUploadDialog(item)} className="gap-2">
                        <FileUp className="h-4 w-4" />
                        Carica
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </StyledCard>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.92rem' }}>
          <Lock size={17} />
          <Typography variant="body2" color="text.secondary">
            Caricamento diretto in piattaforma: nessun invio via WhatsApp o email.
          </Typography>
        </Box>
      </Box>

      <DocumentPreviewDrawer
        open={previewDrawerOpen}
        onClose={handleClosePreviewDrawer}
        fileName={previewFileName}
        fileUrl={previewFileUrl}
        loading={previewLoading}
        details={previewDetails}
        onOpenInNewTab={handleOpenPreviewInNewTab}
        openInNewTabLoading={previewOpenInNewTabLoading}
        onDownload={previewItem ? () => handleDownload(previewItem) : undefined}
        downloadDisabled={!previewItem?.documento_id && !previewItem?.id}
      />

      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle>{selectedTipo?.documento_id ? 'Sostituisci documento' : 'Carica documento'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2.25, pt: '14px !important' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo documento</InputLabel>
            <Select
              label="Tipo documento"
              value={selectedTipoId}
              onChange={(event) => setSelectedTipoId(String(event.target.value || ''))}
            >
              {tipiDocumentoOptions.map((item) => (
                <MenuItem key={item.tipo_documento_id} value={String(item.tipo_documento_id)}>
                  {item.tipo_documento_nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.85 }}>
              File
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="file"
              inputProps={{ accept: '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx' }}
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
            />
            {selectedFile ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {`${selectedFile.name} • ${Math.round(selectedFile.size / 1024)} KB`}
              </Typography>
            ) : null}
          </Box>

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Note documento"
            value={noteDocumento}
            onChange={(event) => setNoteDocumento(event.target.value)}
            placeholder="Eventuali note operative sul documento caricato"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outline" onClick={handleCloseUploadDialog} disabled={uploading}>
            Annulla
          </Button>
          <Button onClick={handleUpload} disabled={uploading} className="gap-2">
            <Upload className="h-4 w-4" />
            {uploading ? 'Caricamento...' : 'Conferma upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Elimina documento</DialogTitle>
        <DialogContent sx={{ pt: '14px !important' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confermi l&apos;eliminazione del documento caricato per <strong>{deleteTarget?.tipo_documento_nome || 'questo tipo documento'}</strong>?
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 180 }}>Tipo documento</TableCell>
                <TableCell>{deleteTarget?.tipo_documento_nome || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
                <TableCell>{deleteTarget?.nome_file || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Esito</TableCell>
                <TableCell>La riga tornerà in stato `mancante` e il file verrà rimosso dallo storage.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outline" onClick={handleCloseDeleteDialog} disabled={deleting}>
            Annulla
          </Button>
          <Button onClick={handleDelete} disabled={deleting} className="gap-2">
            <Trash2 className="h-4 w-4" />
            {deleting ? 'Eliminazione...' : 'Conferma eliminazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
